import { api } from "./client.js";
import type {
  Alert,
  AlertFilters,
  AuditEntry,
  AuditFilters,
  ChainVerification,
  CommandOutcome,
  Door,
  Page,
  PageParams,
  ServiceStatus,
  SweepResult,
  WatchdogStatus,
} from "../types/api.js";

/** Builds a query string, dropping anything unset.
 *
 * Every value goes through URLSearchParams, so a door id or an actor id containing a `&` or a
 * `#` cannot terminate the query early or forge another parameter. Note also that the API now
 * REFUSES a parameter it does not recognise rather than ignoring it - so a typo here is a 422
 * a developer sees immediately, rather than a filter that silently did nothing. */
function queryString(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

export const listDoors = () => api.get<Door[]>("/doors");
export const getDoor = (doorId: string) => api.get<Door>(`/doors/${encodeURIComponent(doorId)}`);

export const openDoor = (doorId: string) =>
  api.post<CommandOutcome>(`/doors/${encodeURIComponent(doorId)}/open`);

export const statusCheck = (doorId: string) =>
  api.post<CommandOutcome>(`/doors/${encodeURIComponent(doorId)}/status-check`);

/** Persistent overrides carry `confirm: true`. The API refuses them without it, so a generic
 * client cannot reach one by a single mis-click. */
export const holdUnlock = (doorId: string) =>
  api.post<CommandOutcome>(`/doors/${encodeURIComponent(doorId)}/hold-unlock`, { confirm: true });

export const holdLock = (doorId: string) =>
  api.post<CommandOutcome>(`/doors/${encodeURIComponent(doorId)}/hold-lock`, { confirm: true });

export const releaseOverride = (doorId: string) =>
  api.post<CommandOutcome>(`/doors/${encodeURIComponent(doorId)}/release-override`);

/** This door's history. Paged and filtered exactly like the audit log, because it IS the audit
 * log with the door fixed by the path. */
export const doorActivity = (
  doorId: string,
  params: PageParams & Omit<AuditFilters, "doorId"> = {},
) =>
  api.get<Page<AuditEntry>>(
    `/doors/${encodeURIComponent(doorId)}/activity${queryString({ limit: 50, ...params })}`,
  );

export const listAlerts = (params: PageParams & AlertFilters = {}) =>
  api.get<Page<Alert>>(`/alerts${queryString({ limit: 50, ...params })}`);

export const acknowledgeAlert = (id: number) => api.post<Alert>(`/alerts/${id}/acknowledge`);

/** The audit log, paged and filtered SERVER-SIDE.
 *
 * This used to take a bare `limit` and nothing else, so the console could reach only the newest
 * rows - about three hours' worth once the watchdog was writing a row per door per sweep. The
 * rest were still in the hash chain and nothing could get at them. Filtering has to happen on
 * the server for the same reason: narrowing rows the client already has answers "is it on this
 * page", not "did it ever happen". */
export const auditLog = (params: PageParams & AuditFilters = {}) =>
  api.get<Page<AuditEntry>>(`/audit${queryString({ limit: 100, ...params })}`);

export const verifyAudit = () => api.get<ChainVerification>("/audit/verify");

export const watchdogStatus = () => api.get<WatchdogStatus>("/watchdog");
export const runSweep = () => api.post<SweepResult>("/watchdog/sweep");

export const serviceStatus = () => api.get<ServiceStatus>("/status");
